import { useEffect, useState, useContext, ChangeEvent } from "react";

import {
    Spinner,
    Toast,
    ToastContainer,
} from "@salesforce/design-system-react";

import { CopyToClipboard } from "react-copy-to-clipboard";

import { Button, Notice, Select } from "@vimeo/iris/components";
import { Option } from "@vimeo/iris/components/inputs/Select/Select.minors";

import { EmbedPreset, Video } from "vimeo";

import VideoSettingsContext from "../../contexts/videoSettingsContext";

import "../settings.scoped.scss";

import { Controls } from "./Controls";
import { Actions } from "./Actions";
import { YourDetails } from "./YourDetails";
import { Customization } from "./Customization";

import { VerticalDivider } from "../../components/VerticalDivider";
import { VimeoPlayer } from "../VimeoPlayer";

import {
    getContentBlockType,
    isContentBlockContext,
} from "../../sessionStorage";

import UserContext from "../../contexts/userContext";

import {
    addEmbedPresetToVideo,
    deleteEmbedPresetFromVideo,
    isEmbedPresetAddedToVideo,
} from "./embedSettings";

import { getVideo } from "../../vimeoClient";
import { getVideoIdFromUri } from "../../common";
import { Link } from "react-router-dom";
import { Text } from "@vimeo/iris/typography";
import {
    EventName,
    getSfmcLocation,
    sendAnalyticsEvent,
} from "../../analytics/common";

const defaultButtonLabel = "Copy embed code";

export function EmbedForWebSettings() {
    const [videoSettingsContext, setVideoSettingsContext] =
        useContext(VideoSettingsContext);

    const [userContextState] = useContext(UserContext);

    const [video, setVideo] = useState<Video>();
    const [embedPresets, setEmbedPresets] = useState<EmbedPreset[]>();
    const [selectedPresetUri, setSelectedPresetUri] = useState<string>("");
    const [checkingIfVideoHasEmbedPreset, setCheckingIfVideoHasEmbedPreset] =
        useState(false);
    const [buttonLabel, setButtonLabel] = useState(defaultButtonLabel);
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);

    async function checkForSelectedEmbedPreset(
        video: Video,
        presets: EmbedPreset[]
    ) {
        setCheckingIfVideoHasEmbedPreset(true);
        // Check if any of the available embedded presets have been
        // added to the current video.
        const promises = [];
        for (let i = 0; i < presets.length; i++) {
            const p = isEmbedPresetAddedToVideo(presets[i].uri, video.uri);
            promises.push(p);
        }

        const result = await Promise.all(promises);
        const enabledPreset = result.filter((r) => r.added);
        if (enabledPreset?.length) {
            setSelectedPresetUri(enabledPreset[0].presetUri);
        }
        setCheckingIfVideoHasEmbedPreset(false);
    }

    useEffect(() => {
        if (!video) {
            return;
        }

        sendAnalyticsEvent(EventName.pageView, {
            name: "video_settings",
            location: getSfmcLocation(),
            video_id: getVideoIdFromUri(video.uri),
            section: "embed_for_web",
        });
    }, [video]);

    useEffect(() => {
        setVideo(videoSettingsContext.video);
        setEmbedPresets(userContextState.presets);
        if (!videoSettingsContext.video || !userContextState.presets) {
            return;
        }

        checkForSelectedEmbedPreset(
            videoSettingsContext.video,
            userContextState.presets
        ).catch(console.error);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [videoSettingsContext, userContextState.presets]);

    function onUpdateVideo(video: Video) {
        setVideoSettingsContext({ ...videoSettingsContext, video });
    }

    function onCopy() {
        setButtonLabel("Copied!");
        setTimeout(() => setButtonLabel(defaultButtonLabel), 800);
    }

    function onError(msg: string) {
        setError(msg);
    }

    async function refreshVideo() {
        if (!video) {
            return;
        }

        const refreshedVideoObj = await getVideo(getVideoIdFromUri(video.uri));
        onUpdateVideo(refreshedVideoObj);
    }

    async function onEmbedPresetChanged(e: ChangeEvent) {
        if (!video) {
            return;
        }

        setLoading(true);
        const presetUri = (e.target as HTMLSelectElement).value;
        const previouslySelectedPresetUri = selectedPresetUri;
        setSelectedPresetUri(presetUri);

        if (previouslySelectedPresetUri) {
            // First delete the previously assigned preset,
            // and then assign the newly selected preset.
            await deleteEmbedPresetFromVideo(
                previouslySelectedPresetUri,
                video.uri
            );
        }

        // If the user selected None, then just refresh the video settings.
        if (!presetUri) {
            await refreshVideo();
            setLoading(false);
            return;
        }

        await addEmbedPresetToVideo(presetUri, video.uri);
        await refreshVideo();
        setLoading(false);
    }

    const embedPresetEls = embedPresets?.map((p) => (
        <Option key={p.uri} value={p.uri}>
            {p.name}
        </Option>
    ));

    return (
        <>
            {loading && <Spinner />}
            {video && (
                <div className="slds-grid">
                    <div className="slds-col">
                        <div className="slds-var-p-bottom_large">
                            {!videoSettingsContext.hideSettingsViewHeading && (
                                <div className="slds-text-heading_medium">
                                    Embed for web
                                </div>
                            )}
                            {isContentBlockContext() &&
                                getContentBlockType() === "web" && (
                                    <Notice format="primary">
                                        This is a preview - the Vimeo player
                                        will be rendered upon Publish.
                                    </Notice>
                                )}
                            <p>
                                Customize the player and add your branding (only
                                applies to embedded videos).
                            </p>
                        </div>
                        {video.privacy.view === "nobody" && (
                            <div className="slds-var-m-bottom_small">
                                <Notice format="primary">
                                    <Text size={200}>
                                        When the privacy is set to "Private",
                                        the embedded video won't be visible to
                                        everyone.
                                    </Text>
                                    &nbsp;
                                    <Link
                                        to="../basic"
                                        style={{ fontSize: "0.875rem" }}
                                    >
                                        Change privacy
                                    </Link>
                                </Notice>
                            </div>
                        )}
                        <CopyToClipboard
                            text={video.embed.html}
                            onCopy={onCopy}
                        >
                            <Button
                                className="slds-var-m-bottom_small"
                                format="primary"
                                style={{ width: "100%" }}
                            >
                                {buttonLabel}
                            </Button>
                        </CopyToClipboard>
                        <div className="slds-form-element slds-var-m-bottom_large">
                            <div className="slds-form-element__control">
                                <Select
                                    disabled={checkingIfVideoHasEmbedPreset}
                                    value={selectedPresetUri}
                                    onChange={onEmbedPresetChanged}
                                >
                                    <Option key={0} value="">
                                        No preset
                                    </Option>
                                    {embedPresetEls}
                                </Select>
                            </div>
                        </div>
                        <div className="slds-border_bottom slds-var-m-vertical_large"></div>
                        <Controls
                            video={video}
                            onUpdate={onUpdateVideo}
                            onError={onError}
                        />
                        <div className="slds-border_bottom slds-var-m-vertical_large"></div>
                        <Actions
                            video={video}
                            onUpdate={onUpdateVideo}
                            onError={onError}
                        />
                        <div className="slds-border_bottom slds-var-m-vertical_large"></div>
                        <YourDetails
                            video={video}
                            onUpdate={onUpdateVideo}
                            onError={onError}
                        />
                        <div className="slds-border_bottom slds-var-m-vertical_large"></div>
                        <Customization
                            video={video}
                            onUpdate={onUpdateVideo}
                            onError={onError}
                        />
                    </div>
                    {!isContentBlockContext() && (
                        <div>
                            <VerticalDivider />
                        </div>
                    )}
                    <div className="slds-col">
                        <VimeoPlayer video={video} />
                    </div>
                    {error && (
                        <ToastContainer>
                            <Toast
                                labels={{
                                    heading: error,
                                }}
                                variant="error"
                                onRequestClose={() => setError("")}
                            />
                        </ToastContainer>
                    )}
                </div>
            )}
        </>
    );
}
